import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TablePagination,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Checkbox,
  Button,
  Toolbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Mail as MailIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import dayjs from 'dayjs';
import AssignAgent from '../../components/common/AssignAgent';
import AgentSelect from '../../components/common/AgentSelect';
import { fetchAgentSelect } from '../../services/common';
import { useLead } from '../../contexts/LeadContext';

const DashboardLeads = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const statType = searchParams.get('stat_type');
  const startDate = searchParams.get('start_date');
  const endDate = searchParams.get('end_date');
  const assignedToFromUrl = searchParams.get('assigned_to');
  
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const { assignAgents } = useLead();
  const isAssigningRef = useRef(false);
  const selectedLeadsForDialogRef = useRef([]);
  const selectedLeadsRef = useRef([]); // Keep ref in sync with selectedLeads state
  
  // Check if this is unassigned leads view
  const isUnassignedLeads = statType === 'unassigned-leads';
  
  // Check if this is flagged leads view (similar to unassigned - no assigned_to filter)
  const isFlaggedLeads = statType === 'flagged-leads';
  
  // Check if this is a site visit type
  const isSiteVisitType = ['site-visit-scheduled', 'site-visit-confirmed', 'site-visit-done'].includes(statType);
  
  // Check if this is total leads view
  const isTotalLeads = statType === 'total-leads';
  
  // Get stat type title mapping
  const statTypeTitles = {
    'total-leads': 'Total Leads',
    'new-leads': 'New Leads',
    'contacted-leads': 'Contacted Leads',
    'connected-leads': 'Connected Leads',
    'hot-leads': 'Hot Leads',
    'unassigned-leads': 'Unassigned Leads',
    'untouched-leads': 'Untouched Leads',
    'no-reminder-leads': 'Leads with No Reminder',
    'details-shared': 'Details Shared',
    'site-visit-scheduled': 'Site Visit Scheduled',
    'site-visit-confirmed': 'Site Visit Confirmed',
    'site-visit-done': 'Site Visit Done',
    'not-interested-leads': 'Leads Marked Not Interested',
    'multiple-visits-leads': 'Multiple Visits Leads',
    'dormant-leads': 'Dormant Leads',
    'hot-warm-at-risk': 'Hot/Warm Leads at Risk',
    'lead-contacted': 'Leads Contacted',
    'lead-connected': 'Leads Connected',
    'bookings-closed': 'Bookings Closed',
    'pending-followup-leads': 'Pending Followups',
    'pending-visit-leads': 'Pending Visits',
    'flagged-leads': 'Flagged Leads',
    'new-followups-scheduled': 'New Followups Scheduled',
  };
  
  const title = statTypeTitles[statType] || 'Leads';
  
  // Fetch agents when component mounts (for assignment)
  useEffect(() => {
    fetchAgentSelect().then(data => {
      setAgents(data || []);
      
      // If assigned_to is in URL, find and set the matching agent
      if (assignedToFromUrl && data && data.length > 0) {
        const agentId = parseInt(assignedToFromUrl, 10);
        if (!isNaN(agentId)) {
          const matchingAgent = data.find(agent => 
            (agent.agent_id && agent.agent_id === agentId) || 
            (agent.id && agent.id === agentId)
          );
          if (matchingAgent && !isUnassignedLeads) {
            setSelectedAgents([matchingAgent]);
          }
        }
      }
    }).catch(error => {
      console.error('Error fetching agents:', error);
    });
  }, [assignedToFromUrl, isUnassignedLeads]);
  
  // Keep ref in sync with selectedLeads state
  useEffect(() => {
    selectedLeadsRef.current = selectedLeads;
  }, [selectedLeads]);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Clear assigned agents filter when switching to unassigned-leads
  useEffect(() => {
    if (isUnassignedLeads && selectedAgents.length > 0) {
      setSelectedAgents([]);
    }
  }, [isUnassignedLeads]);

  // Handle assigned_to URL parameter changes
  useEffect(() => {
    if (!assignedToFromUrl) {
      // Clear selected agents if assigned_to is removed from URL
      if (selectedAgents.length > 0 && !isUnassignedLeads) {
        // Only clear if it was set from URL (we can't distinguish, so be conservative)
        // Actually, let's not auto-clear here to avoid clearing user selections
      }
      return;
    }

    // If assigned_to is in URL and agents are loaded, find and set the matching agent
    if (agents && agents.length > 0 && !isUnassignedLeads) {
      const agentId = parseInt(assignedToFromUrl, 10);
      if (!isNaN(agentId)) {
        const matchingAgent = agents.find(agent => 
          (agent.agent_id && agent.agent_id === agentId) || 
          (agent.id && agent.id === agentId)
        );
        if (matchingAgent) {
          // Only update if not already set to avoid unnecessary re-renders
          const currentAgentIds = selectedAgents.map(a => a.agent_id || a.id);
          if (!currentAgentIds.includes(agentId)) {
            setSelectedAgents([matchingAgent]);
          }
        }
      }
    }
  }, [assignedToFromUrl, agents, isUnassignedLeads, selectedAgents]);
  
  // Fetch leads from backend
  useEffect(() => {
    if (!statType || !startDate || !endDate) {
      setLoading(false);
      return;
    }
    
    const fetchLeads = async () => {
      setLoading(true);
      try {
        const requestData = {
          stat_type: statType,
          date_range: {
            start_date: startDate,
            end_date: endDate,
          },
          page: page + 1,
          page_size: rowsPerPage,
          sort_field: sortField,
          sort_order: sortOrder,
          search: debouncedSearchTerm,
        };
        
        // Add assigned_to filter for all stat_type except unassigned-leads and flagged-leads
        // Priority: selectedAgents from UI > assigned_to from URL
        if (!isUnassignedLeads && !isFlaggedLeads) {
          if (selectedAgents.length > 0) {
            requestData.assigned_to = selectedAgents
              .map(agent => agent.agent_id || agent.id)
              .filter(id => id != null);
          } else if (assignedToFromUrl) {
            // Fallback to URL parameter if no agents selected in UI
            const agentId = parseInt(assignedToFromUrl, 10);
            if (!isNaN(agentId)) {
              requestData.assigned_to = [agentId];
            }
          }
        }
        
        // Use leads_v2 endpoint which handles all stat_type filters including flagged-leads
        const response = await api.post('/api/lms/leads/v2/', requestData);
        
        if (response.data) {
          const newLeads = response.data.results || [];
          setLeads(newLeads);
          setTotalCount(response.data.count || 0);
          
          // Filter out selected leads that are no longer in the current list
          // This happens when leads are assigned and removed from unassigned leads list
          // Only do this if we're not in the middle of an assignment
          if (!isAssigningRef.current) {
            setSelectedLeads(prev => {
              const currentLeadIds = new Set(newLeads.map(lead => lead.id));
              return prev.filter(id => currentLeadIds.has(id));
            });
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard leads:', error);
        setLeads([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeads();
  }, [statType, startDate, endDate, page, rowsPerPage, sortField, sortOrder, debouncedSearchTerm, refreshKey, selectedAgents, isUnassignedLeads]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSelectedLeads([]); // Clear selection when page changes
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSelectedLeads([]); // Clear selection when rows per page changes
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
    setSelectedLeads([]); // Clear selection when search changes
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
    setSelectedLeads([]); // Clear selection when clearing search
  };

  // Handle lead selection
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const allLeadIds = leads.map(lead => lead.id);
      setSelectedLeads(allLeadIds);
    } else {
      setSelectedLeads([]);
    }
  };

  const handleLeadSelect = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleOpenAssignDialog = (leadId = null, event = null) => {
    // Prevent any default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Use ref to get the most current selection value (avoids stale closure issues)
    const currentSelectedLeads = selectedLeadsRef.current;
    const currentSelection = leadId ? [leadId] : [...currentSelectedLeads];
    
    // If no selection and no leadId, don't open dialog
    if (currentSelection.length === 0) {
      console.warn('No leads selected');
      return;
    }
    
    // Preserve selection in dialog ref immediately (synchronously)
    selectedLeadsForDialogRef.current = currentSelection;
    
    // Mark that we're starting an assignment process
    isAssigningRef.current = true;
    
    // If a specific lead ID is provided, update selection
    if (leadId) {
      setSelectedLeads([leadId]);
    }
    
    // Open dialog - this might trigger re-renders, but ref is already set
    setAgentDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAgentDialogOpen(false);
    // Reset assignment flag when dialog closes (user cancelled)
    isAssigningRef.current = false;
    // Clear the ref when dialog closes
    selectedLeadsForDialogRef.current = [];
  };

  const handleAssignmentComplete = async (agentId) => {
    // Mark that assignment is complete
    isAssigningRef.current = false;
    
    // Close the dialog
    setAgentDialogOpen(false);
    
    // Trigger refetch by updating refreshKey
    setRefreshKey(prev => prev + 1);
    
    // After data refreshes, the useEffect will automatically filter out
    // selected leads that are no longer in the list (assigned leads disappear from unassigned list)
    // This way selection is preserved for leads that are still in the list
  };

  // Check if all visible leads are selected
  const allSelected = leads.length > 0 && selectedLeads.length === leads.length;
  const someSelected = selectedLeads.length > 0 && selectedLeads.length < leads.length;
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return dayjs(dateString).format('MMM DD, YYYY HH:mm');
    } catch {
      return dateString;
    }
  };

  // Get initials helper
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
    // Reset to first page when sorting changes
    setPage(0);
  };

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return null;
    }
    return sortOrder === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };
  
  if (!statType || !startDate || !endDate) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Missing required parameters. Please navigate from a dashboard card.
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() => navigate('/dashboard')}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {startDate} to {endDate}
          </Typography>
        </Box>
        {!loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Total: {totalCount} leads
          </Typography>
        )}
      </Box>

      {/* Search Bar and Filters */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 2, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          p: 1.5
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextField
          fullWidth
          placeholder="Search by name, lead ID, email, or mobile number..."
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            }
          }}
        />
          {/* Assigned To Filter - Only show for non-unassigned leads */}
          {!isUnassignedLeads && (
            <Box sx={{ minWidth: { xs: '100%', sm: 300 } }}>
              <AgentSelect
                multiple={true}
                value={selectedAgents.map(agent => agent.agent_id || agent.id)}
                onChange={(selectedIds, selectedAgentObjects) => {
                  setSelectedAgents(selectedAgentObjects || []);
                  setPage(0); // Reset to first page when filter changes
                }}
                label="Assigned To"
                agents={agents}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Bulk Action Bar - Only show for unassigned leads */}
      {isUnassignedLeads && selectedLeads.length > 0 && (
        <Paper 
          elevation={2}
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.main',
            backgroundColor: 'primary.light',
            backgroundColor: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(25, 118, 210, 0.2)' 
              : 'rgba(25, 118, 210, 0.08)',
          }}
        >
          <Toolbar sx={{ minHeight: '56px !important', px: 2 }}>
            <Typography sx={{ flex: 1 }} variant="body1" fontWeight={600}>
              {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => handleOpenAssignDialog(null, e)}
              sx={{ ml: 2 }}
            >
              Assign Agent
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSelectedLeads([])}
              sx={{ ml: 1 }}
            >
              Clear Selection
            </Button>
          </Toolbar>
        </Paper>
      )}

      {/* Pagination and Count at Top */}
      {!loading && totalCount > 0 && (
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount} leads
            </Typography>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Rows per page:"
              sx={{ 
                border: 'none',
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: 0,
                  paddingRight: 0,
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                }
              }}
            />
          </Box>
        </Paper>
      )}
      
      {/* Leads Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflowX: 'auto' }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[50] }}>
                  {isUnassignedLeads && (
                    <TableCell padding="checkbox" sx={{ pl: 2 }}>
                      <Checkbox
                        color="primary"
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={handleSelectAllClick}
                        inputProps={{
                          'aria-label': 'select all leads',
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                    onClick={() => handleSort('lead_id')}
                  >
                    Lead ID {renderSortIcon('lead_id')}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                    onClick={() => handleSort('name')}
                  >
                    Name {renderSortIcon('name')}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                    onClick={() => handleSort('contact_info')}
                  >
                    Contact Info {renderSortIcon('contact_info')}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                    onClick={() => handleSort('created_at')}
                  >
                    Created At {renderSortIcon('created_at')}
                  </TableCell>
                  {isTotalLeads && (
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                      }}
                      onClick={() => handleSort('next_followup_or_site_visit')}
                    >
                      Next Followup/Site Visit {renderSortIcon('next_followup_or_site_visit')}
                    </TableCell>
                  )}
                  {isSiteVisitType ? (
                    <>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600, 
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                        }}
                        onClick={() => handleSort('last_contacted_at')}
                      >
                        Last Visit Scheduled {renderSortIcon('last_contacted_at')}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600, 
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                        }}
                        onClick={() => handleSort('last_contacted_at')}
                      >
                        Last Visit Completed {renderSortIcon('last_contacted_at')}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                      }}
                      onClick={() => handleSort('last_contacted_at')}
                    >
                      Last Contacted At {renderSortIcon('last_contacted_at')}
                    </TableCell>
                  )}
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                    onClick={() => handleSort('stage')}
                  >
                    Stage {renderSortIcon('stage')}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                    onClick={() => handleSort('status')}
                  >
                    Status {renderSortIcon('status')}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
                    }}
                    onClick={() => handleSort('sales_owner')}
                  >
                    Sales Owner {renderSortIcon('sales_owner')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={
                      isUnassignedLeads 
                        ? (isTotalLeads ? 10 : (isSiteVisitType ? 10 : 9)) 
                        : (isTotalLeads ? 9 : (isSiteVisitType ? 9 : 8))
                    } sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No leads found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => {
                    const leadName = lead.contact?.name || 'N/A';
                    const leadEmail = lead.contact?.email || '';
                    const leadMobile = lead.contact?.mobile || '';
                    const salesOwner = lead.agent?.name || 'Unassigned';
                    const salesOwnerInitials = lead.agent?.name ? getInitials(lead.agent.name) : '?';
                    const isSelected = selectedLeads.includes(lead.id);
                    
                    return (
                      <TableRow
                        key={lead.id}
                        hover
                        selected={isSelected}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.action.hover,
                          },
                          '&.Mui-selected': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(25, 118, 210, 0.16)'
                              : 'rgba(25, 118, 210, 0.08)',
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(25, 118, 210, 0.24)'
                                : 'rgba(25, 118, 210, 0.12)',
                            },
                          },
                        }}
                        onClick={(e) => {
                          // Don't navigate if clicking checkbox
                          if (e.target.type === 'checkbox') return;
                          navigate(`/leads/${lead.id}`);
                        }}
                      >
                        {isUnassignedLeads && (
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              color="primary"
                              checked={isSelected}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLeadSelect(lead.id);
                              }}
                              inputProps={{
                                'aria-labelledby': `lead-${lead.id}`,
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {lead.lead_id ? `#${lead.lead_id}` : `#${lead.id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            component={Link}
                            to={`/leads/${lead.id}`}
                            variant="body2" 
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              
                              // If Ctrl/Cmd is pressed, open in new tab
                              if (e.ctrlKey || e.metaKey) {
                                e.preventDefault(); // Prevent Link navigation
                                window.open(`/leads/${lead.id}`, '_blank');
                                return;
                              }
                              // Regular click - Link component will handle navigation
                            }}
                            sx={{ 
                              fontWeight: 500,
                              color: 'primary.main',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.dark'
                              }
                            }}
                          >
                            {leadName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {leadMobile && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {leadMobile}
                                </Typography>
                              </Box>
                            )}
                            {leadEmail && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <MailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {leadEmail}
                                </Typography>
                              </Box>
                            )}
                            {!leadMobile && !leadEmail && (
                              <Typography variant="caption" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(lead.created_at)}
                          </Typography>
                        </TableCell>
                        {isTotalLeads && (
                          <TableCell>
                            {lead.next_followup_or_site_visit ? (
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {lead.next_followup_or_site_visit.date} {lead.next_followup_or_site_visit.time}
                                </Typography>
                                <Chip
                                  label={lead.next_followup_or_site_visit.type_verbose}
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: lead.next_followup_or_site_visit.type === 'site_visit' 
                                      ? (theme) => theme.palette.success.light 
                                      : (theme) => theme.palette.info.light,
                                    color: lead.next_followup_or_site_visit.type === 'site_visit' 
                                      ? (theme) => theme.palette.success.dark 
                                      : (theme) => theme.palette.info.dark,
                                  }}
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        {isSiteVisitType ? (
                          <>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {lead.site_visit?.last_scheduled 
                                  ? `${lead.site_visit.last_scheduled.date} ${lead.site_visit.last_scheduled.time}`
                                  : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {lead.site_visit?.last_completed 
                                  ? `${lead.site_visit.last_completed.date} ${lead.site_visit.last_completed.time}`
                                  : 'N/A'}
                              </Typography>
                            </TableCell>
                          </>
                        ) : (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {lead.last_contact_at ? formatDate(lead.last_contact_at) : 'Never'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={lead.stage_verbose || lead.stage || 'N/A'}
                            size="small"
                            sx={{
                              backgroundColor: (theme) => {
                                const stageColors = {
                                  'new': theme.palette.info.light,
                                  'contacted': theme.palette.warning.light,
                                  'qualified': theme.palette.success.light,
                                  'negotiation': theme.palette.primary.light,
                                  'closure': theme.palette.success.main,
                                  'junk': theme.palette.error.light,
                                };
                                return stageColors[lead.stage] || theme.palette.grey[300];
                              },
                              color: (theme) => {
                                const stageTextColors = {
                                  'new': theme.palette.info.dark,
                                  'contacted': theme.palette.warning.dark,
                                  'qualified': theme.palette.success.dark,
                                  'negotiation': theme.palette.primary.dark,
                                  'closure': theme.palette.success.contrastText,
                                  'junk': theme.palette.error.dark,
                                };
                                return stageTextColors[lead.stage] || theme.palette.text.primary;
                              },
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.status_verbose || lead.status || 'N/A'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell
                          onClick={(e) => {
                            e.stopPropagation();
                            if (salesOwner === 'Unassigned') {
                              // Open assign dialog for unassigned leads
                              handleOpenAssignDialog(lead.id);
                            } else if (lead.agent?.id) {
                              // Navigate to agent report page for assigned agents
                              navigate(`/reports/agent/${lead.agent.id}`);
                            }
                          }}
                          sx={{
                            cursor: (salesOwner === 'Unassigned' || lead.agent?.id) ? 'pointer' : 'default',
                            '&:hover': (salesOwner === 'Unassigned' || lead.agent?.id) ? {
                              backgroundColor: (theme) => theme.palette.action.hover,
                            } : {},
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: '0.75rem',
                                bgcolor: salesOwner === 'Unassigned' 
                                  ? (theme) => theme.palette.grey[400]
                                  : (theme) => theme.palette.primary.main,
                              }}
                            >
                              {salesOwnerInitials}
                            </Avatar>
                            <Typography 
                              variant="body2" 
                              color={salesOwner === 'Unassigned' ? 'primary' : 'text.secondary'}
                              sx={{
                                fontWeight: (salesOwner === 'Unassigned' || lead.agent?.id) ? 500 : 400,
                                textDecoration: (salesOwner === 'Unassigned' || lead.agent?.id) ? 'underline' : 'none',
                                '&:hover': (salesOwner === 'Unassigned' || lead.agent?.id) ? {
                                  textDecoration: 'underline',
                                } : {},
                              }}
                            >
                              {salesOwner}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Assign Agent Dialog - Available for all views when assigning */}
      {agentDialogOpen && (
        <AssignAgent
          open={agentDialogOpen}
          onClose={handleCloseAssignDialog}
          selectedLeadIds={selectedLeadsForDialogRef.current.length > 0 
            ? selectedLeadsForDialogRef.current 
            : selectedLeads}
          currentAgent={null}
          onAssign={handleAssignmentComplete}
        />
      )}
    </Box>
  );
};

export default DashboardLeads;

